import {
  Button,
  OutlinedSelect,
  SearchBox,
  SelectOptionType,
  useMobile,
} from '@octano/global-ui';
import { ReactElement } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col, Form, Row } from 'reactstrap';

const dictPrefix =
  'financialPortal.currentAccount.tabs.agreedPayments.searchControls';

export interface AgreedPaymentsIndividualFilters {
  concept?: string;
  studyPlanId?: number;
}

interface FormValues {
  concept: string;
  studyPlanId: SelectOptionType | null;
}

const DEFAULT_FORM_VALUES: FormValues = {
  concept: '',
  studyPlanId: null,
};

interface Props {
  programs: SelectOptionType[];
  isDownloadLoading?: boolean;
  onSearch: (sp: AgreedPaymentsIndividualFilters) => void;
  onClear: () => void;
  onDownload: () => void;
}

export function SearchControls({
  programs,
  isDownloadLoading = false,
  onSearch,
  onClear,
  onDownload,
}: Props): ReactElement {
  const { t } = useTranslation();
  const isMobile = useMobile();
  const { handleSubmit, control, reset, setValue } = useForm<FormValues>({
    defaultValues: DEFAULT_FORM_VALUES,
  });

  const submit = (values: FormValues): void => {
    onSearch({
      concept: values.concept?.trim() || undefined,
      studyPlanId: (values.studyPlanId?.value as number) || undefined,
    });
  };

  const handleClear = (): void => {
    reset(DEFAULT_FORM_VALUES);
    onClear();
  };

  return (
    <Form onSubmit={handleSubmit(submit)}>
      <Row className="gutters-filters">
        <Col xs={12} md={4}>
          <SearchBox
            name="concept"
            control={control}
            label={t(`${dictPrefix}.concept.label`)}
            placeholder={t(`${dictPrefix}.concept.placeholder`)}
            clear={() => setValue('concept', '')}
          />
        </Col>
        <Col xs={12} md={4}>
          <OutlinedSelect
            name="studyPlanId"
            control={control}
            label={t(`${dictPrefix}.program.label`)}
            placeholder={t(`${dictPrefix}.program.placeholder`)}
            options={programs}
            disabled={!programs.length}
          />
        </Col>
        <Col xs={12} md={4}>
          <div className="d-flex justify-content-end">
            <Button
              type="submit"
              text={t(`${dictPrefix}.search`)}
              size="sm"
              style={{
                width: '100%',
                height: 41,
                marginTop: isMobile ? 0 : '1.5rem',
              }}
            />
            <Button
              type="button"
              text={t(`${dictPrefix}.clear`)}
              size="sm"
              outlined
              onClick={handleClear}
              style={{
                width: '100%',
                height: 41,
                marginTop: isMobile ? '1rem' : '1.5rem',
                marginLeft: 10,
              }}
            />
          </div>
        </Col>
      </Row>
      <Row className="pt-3 pb-4">
        <Col xs={12} md={{ size: 4, offset: 8 }}>
          <Button
            type="button"
            icon="download"
            text={t(`${dictPrefix}.download`)}
            size="sm"
            outlined
            loading={isDownloadLoading}
            onClick={onDownload}
            style={{ width: '100%', height: 41 }}
          />
        </Col>
      </Row>
    </Form>
  );
}
